import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { acceptPrivacy } from "@/api/auth";
import appLogo from "@/images/notanaicon.svg";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingSpinner } from "@/components/LoadingSpinner";

export function PrivacyStatement() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { isAuthLoading } = useAuth();
  const from = location.state?.from?.pathname || "/app/meetings";
  
  // This component assumes the user needs to accept privacy
  // because the ProtectedRoute already did the check and redirected here
  // No need to recheck and potentially cause a redirect loop
  useEffect(() => {
    setIsLoading(false);
  }, []);

  const handleAccept = async () => {
    console.log("Accept button clicked");
    setIsSubmitting(true);
    try {
      console.log("Sending privacy acceptance to server...");
      await acceptPrivacy();
      console.log("Privacy acceptance successful");
      
      // Navigate immediately to target destination
      console.log("Navigating to:", from);
      navigate(from, { replace: true });
      
      // Refresh privacy status after navigation has started
      // This ensures ProtectedRoute will have the updated status on next render
      setTimeout(() => {
        // @ts-ignore - This function was added to window in ProtectedRoute
        if (window.__refreshPrivacyStatus) {
          console.log("Triggering privacy status refresh");
          // @ts-ignore
          window.__refreshPrivacyStatus();
        }
      }, 100);
    } catch (error) {
      console.error("Failed to accept privacy statement:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save your privacy preferences. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isAuthLoading || isLoading) {
    return <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner />
    </div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden p-4">
      {/* Animated Background Gradient Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Base gradient layer */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-slate-100 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-indigo-950"></div>
        
        <div className="absolute top-0 -left-40 w-96 h-96 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 dark:opacity-30 animate-blob" />
        <div className="absolute top-0 -right-40 w-96 h-96 bg-gradient-to-r from-indigo-400 to-violet-400 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 dark:opacity-30 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-96 h-96 bg-gradient-to-r from-violet-400 to-purple-400 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 dark:opacity-30 animate-blob animation-delay-4000" />
      </div>

      {/* Grain Texture Overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')] opacity-40 dark:opacity-20 pointer-events-none" />

      <Card className="w-full max-w-3xl relative z-10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <img src={appLogo} alt="Notana Logo" className="h-8 w-8 dark:invert" />
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Privacy Statement</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <h2 className="text-xl font-semibold">Privacy Policy for Notana</h2>
          
          <p>Last updated: August 13, 2025</p>
          
          <div className="space-y-4 text-sm max-h-80 overflow-y-auto p-4 border rounded-md">
            <p>Welcome to Notana. We respect your privacy and are committed to protecting your personal data.</p>
            
            <h3 className="text-lg font-medium">1. Information We Collect</h3>
            <p>We collect information you provide directly to us, including:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Email address for authentication</li>
              <li>Meeting data, agenda items, and action items you create</li>
              <li>Usage information and interactions with our service</li>
            </ul>
            
            <h3 className="text-lg font-medium">2. How We Use Your Information</h3>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide, maintain, and improve our services</li>
              <li>Authenticate your identity and provide secure access</li>
              <li>To advise you of changes to our service that may affect you</li>
            </ul>
            
            <h3 className="text-lg font-medium">3. Sharing Your Information</h3>
            <p>We share information with:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Other users you explicitly share meetings with</li>
              <li>As required by law or to protect rights and safety</li>
            </ul>
            <p>We do NOT share information with:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Any third parties including advertisers, marketing or lead generation companies</li>
            </ul>
            
            <h3 className="text-lg font-medium">4. Data Security</h3>
            <p>We implement appropriate security measures to protect your personal information.</p>
            
            <h3 className="text-lg font-medium">5. Cookies</h3>
            <p>We use a single, first-party session cookie to store a JSON Web Token (JWT) that enables secure login to our app. This cookie is strictly necessary for the app’s functionality and is automatically deleted when you log out.</p>
            <p>We do not use cookies for tracking, analytics, or marketing purposes.</p>
            
            <h3 className="text-lg font-medium">6. Changes to This Policy</h3>
            <p>We may update this policy from time to time. We will notify you of any changes by posting the updated policy on this page and making it visible to you when you next login to the service.</p>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleAccept} disabled={isSubmitting} className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40">
            {isSubmitting ? "Processing..." : "I Accept"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
